import type { Middleware } from "@reduxjs/toolkit";

/**
 * Middleware that translates RTK question actions to legacy action strings.
 *
 * This is needed during the transition period when app stores still have legacy
 * reducers (NotebookDataReducer, etc.) that listen for legacy action strings
 * to perform cross-reducer side effects (e.g., marking notebook components complete).
 *
 * Usage: Add to app store middleware chain:
 *   middleware: (getDefaultMiddleware) =>
 *     getDefaultMiddleware().concat(questionActionTranslator)
 *
 * TODO: Remove this middleware when all legacy reducers are migrated to RTK.
 */

const ACTION_MAP: Record<string, string> = {
  "cscq/selectOption": "CSCQ/Select Option",
  "cscq/submit": "CSCQ/Submit",
  "cscq/reset": "CSCQ/Reset",
  "mscq/selectOption": "MSCQ/Select Option",
  "mscq/submit": "MSCQ/Submit",
  "mscq/reset": "MSCQ/Reset",
  "mscq/enableAll": "MSCQ/Enable All",
  "oeq/update": "UPDATE_OEQ",
  "oeq/submit": "SUBMIT_OEQ",
  "oeq/reset": "RESET_OEQ",
  "anatomy/setAnswer": "ANATOMY/Set Answer",
  "anatomy/submit": "ANATOMY/Submit",
  "anatomy/reset": "ANATOMY/Reset",
};

/**
 * Flag added to translated actions so RTK slice extraReducers can skip them,
 * preventing double-handling (critical for toggle actions like MSCQ/Select Option).
 */
export const LEGACY_TRANSLATED_FLAG = "_fromTranslator";

export const questionActionTranslator: Middleware =
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (_store) => (next) => (action: any) => {
    const result = next(action);

    const legacyType = ACTION_MAP[action.type];
    if (legacyType) {
      next({ ...action, type: legacyType, [LEGACY_TRANSLATED_FLAG]: true });
    }

    return result;
  };
